/**
 * grid.ts — the 2.5 mm engineering grid, for DISPLAY only.
 *
 * The grid constant is duplicated here rather than imported, because importing
 * it would mean importing Engine source, which the repository boundary forbids.
 * It is duplicated safely: this copy draws grid lines and positions a drag
 * ghost, and is never the source of a coordinate that reaches the server.
 *
 * When the user drops a symbol, the client sends an INTENT — "place this here"
 * — and the Engine resolves the actual grid-aligned, collision-free position
 * and sends it back in the projection. If this constant were ever wrong, the
 * ghost would sit slightly off and the placed symbol would still be correct.
 * That is the failure mode a boundary is supposed to produce.
 */

export const DISPLAY_GRID_MM = 2.5;

/** Snap a world point to the display grid, for ghost feedback. */
export function snapForDisplay(point: { x: number; y: number }, gridMm = DISPLAY_GRID_MM): { x: number; y: number } {
  return {
    x: Math.round(point.x / gridMm) * gridMm,
    y: Math.round(point.y / gridMm) * gridMm,
  };
}

/**
 * The grid step to draw at a given zoom, in mm.
 *
 * Drawing 2.5 mm lines on an A1 sheet zoomed out is ~330 lines of visual noise
 * that hides the drawing, so the step coarsens until the lines are at least
 * `minPixels` apart.
 */
export function gridStepForScale(scale: number, minPixels = 8): number {
  let step = DISPLAY_GRID_MM;
  while (step * scale < minPixels && step < 1000) step *= 2;
  return step;
}
